// Човешки прочит на действията в „Резервации": създаване, редакция,
// настаняване (към коя сметка / нова), „Тръгнаха си", отмяна на
// настаняването, отказ и връщане.
//
// Данните са в ОТГОВОРА — всяка мутация връща самата резервация (гост, маси,
// час). Неуспешна заявка (409) няма резервация в отговора; тогава
// създаването/редакцията се четат от формата в заявката, а действията остават
// без факти (причината показва статусът на записа).
//
// Действие, направено БЕЗ интернет на касата, облакът записва при replay-а
// със същия път, метод и отговор като онлайн заявката (BE
// audit_logs/replayed.py); различава се само по хедъра `x-edge-event-id`.
// Прочитът е същият, плюс факт „Източник".

import type { TFunction } from "i18next";
import type { AuditLogDetail } from "../../../../api/auditLogs";
import type { HumanStory, Tone } from "./story";
import { asArr, asObj, asString, emptyStory } from "./story";
import { toNumber } from "./format";
import { MODULE_PATHS, RESERVATION_ACTION_KIND, RSV } from "./contract";

/** Категорията на запис под `/reservations` (null — не е резервация). */
export function classifyReservationAction(method: string, path: string): string | null {
  if (method === "POST" && path === MODULE_PATHS.reservationsCollection) return "resCreate";
  if (method === "PATCH" && MODULE_PATHS.reservationItem.test(path)) return "resEdit";
  const action = method === "POST" ? path.match(MODULE_PATHS.reservationAction) : null;
  if (action) return RESERVATION_ACTION_KIND[action[2]] ?? "generic";
  return null;
}

/** Тонът на категориите по резервации (null — не е резервация). */
export function reservationKindTone(kind: string): Tone | null {
  switch (kind) {
    case "resCreate":
    case "resSeat":
      return "add";
    case "resCancel":
      return "remove";
    case "resEdit":
    case "resRelease":
    case "resUnseat":
    case "resRestore":
      return "info";
    default:
      return null;
  }
}

/** Бележката под фактите — какво означава действието (сметките не се пипат). */
const NOTE_KEY: Record<string, string> = {
  resRelease: "reservationReleasedNote",
  resUnseat: "reservationUnseatedNote",
  resCancel: "reservationCancelledNote",
  resRestore: "reservationRestoredNote",
};

const pad = (value: number) => String(value).padStart(2, "0");

/** Хедърът, с който replay-ът на касата маркира своите редове (BE: replayed.py). */
const EDGE_EVENT_HEADER = "x-edge-event-id";

/** Записът е от replay-а на касата (действие без интернет). */
export function isEdgeReplayedEntry(detail: AuditLogDetail): boolean {
  const eventId = detail.request_headers?.[EDGE_EVENT_HEADER];
  return typeof eventId === "string" && eventId !== "";
}

/** „25.09.2026 19:30" — локалното време на терминала. */
export function reservationWhen(iso: string): string | null {
  const moment = Date.parse(iso);
  if (!Number.isFinite(moment)) return null;
  const date = new Date(moment);
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/** „2 ч", „1 ч 30 мин", „45 мин" — същите форми като в модула. */
function durationText(totalMinutes: number, t: TFunction): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return t("reservations.duration.minutes", { minutes });
  if (minutes === 0) return t("reservations.duration.hours", { hours });
  return t("reservations.duration.hoursMinutes", { hours, minutes });
}

export function interpretReservation(
  kind: string,
  detail: AuditLogDetail,
  t: TFunction,
): HumanStory | null {
  const tone = reservationKindTone(kind);
  if (tone === null) return null;
  const human = (key: string, values?: Record<string, unknown>) =>
    t(`auditLogDetail.human.${key}`, values);
  const story = emptyStory(kind, tone);

  // Резервацията от отговора; при неуспешно създаване/редакция — формата.
  const response = asObj(detail.response_body);
  const fromResponse = response && asString(response[RSV.guestName]) ? response : null;
  const isForm = kind === "resCreate" || kind === "resEdit";
  const source = fromResponse ?? (isForm ? asObj(detail.request_body) : null);
  if (!source) {
    story.interpreted = false;
    return story;
  }

  const guestName = asString(source[RSV.guestName]);
  if (guestName) story.facts.push({ label: human("reservationGuest"), value: guestName });

  // Имената на масите са само в отговора; формата носи id-тата им.
  const tableNames = asArr(source[RSV.tableNames]).filter((name): name is string => typeof name === "string");
  if (tableNames.length > 0) {
    story.facts.push({ label: human("reservationTables"), value: tableNames.join(", ") });
  }

  const when = asString(source[RSV.startAt]);
  const whenText = when ? reservationWhen(when) : null;
  if (whenText) story.facts.push({ label: human("reservationTime"), value: whenText });

  const guests = toNumber(source[RSV.guests]);
  if (isForm && guests !== null) {
    story.facts.push({ label: human("reservationGuests"), value: t("reservations.counts.guests", { count: guests }) });
  }
  const duration = toNumber(source[RSV.durationMinutes]);
  if (isForm && duration !== null) {
    story.facts.push({ label: human("reservationDuration"), value: durationText(duration, t) });
  }

  if (kind === "resSeat" && fromResponse) {
    const billId = toNumber(fromResponse[RSV.billId]);
    const created = fromResponse[RSV.billCreated];
    // „Отделна сметка за всяка маса" — всички сметки (главната първа).
    const billIds = asArr(fromResponse[RSV.billIds])
      .map((value) => toNumber(value))
      .filter((value): value is number => value !== null);
    if (billIds.length > 1) {
      story.facts.push({
        label: human("reservationBills"),
        value: human("reservationBillsSeparate", { ids: billIds.map((id) => `№ ${id}`).join(", ") }),
        tone: "add",
      });
    } else if (billId !== null) {
      story.facts.push({
        label: human("reservationBill"),
        value: human(created === true ? "reservationBillNew" : "reservationBillAttached", { id: billId }),
        tone: "add",
      });
    }
  }

  const note = isForm ? asString(source[RSV.note]) : null;
  if (note) story.note = note;
  else if (kind === "resUnseat" && fromResponse?.[RSV.billClosed] === true) {
    // Празната сметка от „Настани" е премахната заедно с отмяната.
    story.note = human("reservationUnseatedBillClosedNote");
  } else if (NOTE_KEY[kind]) story.note = human(NOTE_KEY[kind]);

  story.interpreted = story.facts.length > 0;
  if (isEdgeReplayedEntry(detail)) {
    story.facts.push({ label: human("reservationOrigin"), value: human("reservationOriginEdge") });
  }
  return story;
}

/**
 * „Настани", което е ОТВОРИЛО нова сметка — началото на живота ѝ в
 * хронологията (както `POST /bills` с `billId === 0`).
 */
export function isSeatOpeningNewBill(detail: AuditLogDetail | undefined): boolean {
  if (!detail || detail.method !== "POST") return false;
  if (classifyReservationAction(detail.method, detail.path) !== "resSeat") return false;
  return asObj(detail.response_body)?.[RSV.billCreated] === true;
}
